import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider,
    TextField,
} from '@mui/material';
import {
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip,
    Legend as RechartsLegend,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    // @ts-ignore
} from 'recharts';
import { ResponsiveHeatMap } from '@nivo/heatmap';
import { ResponsiveSankey } from '@nivo/sankey';
import { useDietaryPreferences } from 'providers/DietaryPreferenceProvider';
import { useApi } from 'providers/ApiProvider';

interface AnalyticsData {
    metadata: { totalUsers: number };
    allergies: {
        prevalence: Record<string, number>;
        profiles: Record<string, number>;
        coOccurrence: Record<string, number>;
    };
    diets: {
        prevalence: Record<string, number>;
        profiles: Record<string, number>;
        coOccurrence: Record<string, number>;
    };
    combined: {
        complexity: Record<string, number>;
        profiles: Record<string, number>;
        crossTab: Record<string, Record<string, number>>;
    };
}

const COLORS = {
    Easy: '#4caf50',
    Moderate: '#ff9800',
    Complex: '#f44336',
};

const CHART_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7f50', '#00bcd4', '#e91e63'];

const NO_PREFERENCES = 'No Preferences';

// Nivo Theme for dark tooltips
const nivoTheme = {
    tooltip: {
        container: {
            background: '#000000',
            color: '#ffffff',
            fontSize: '14px',
            borderRadius: '4px',
            boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
            padding: '8px 12px'
        }
    }
};

const DietaryAnalyticsDashboard: React.FC = () => {
    const api = useApi();
    const { restaurantId } = useParams<{ restaurantId: string }>();
    const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
    const [visits, setVisits] = useState<any | null>(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedMonth, setSelectedMonth] = useState(() => {
        const queryParams = new URLSearchParams(location.search);
        const urlMonth = queryParams.get('selectedMonth');
        // Validate YYYY-MM format
        const isValid = /^\d{4}-\d{2}$/.test(urlMonth || '');

        if (isValid && urlMonth) {
            return urlMonth;
        }

        const now = new Date();
        now.setMonth(now.getMonth() - 1); // Default to previous month
        const y = now.getFullYear();
        const m = String(now.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    });

    const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newMonth = e.target.value;
        setSelectedMonth(newMonth);
        navigate({
            pathname: location.pathname,
            search: `?selectedMonth=${newMonth}`
        });
    };

    const { allergyKeyToName, dietKeyToName } = useDietaryPreferences();

    useEffect(() => {
        const fetchData = async () => {
            if (!restaurantId) return;
            setLoading(true);
            try {
                // Calculate start and end date from selectedMonth
                const [year, month] = selectedMonth.split('-');
                const start = new Date(parseInt(year), parseInt(month) - 1, 1);
                const end = new Date(parseInt(year), parseInt(month), 0); // Last day of the month

                // Format dates as YYYY-MM-DD for API
                const formatDate = (d: Date) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                };

                const visits = await api.getRestaurantVisits(
                    restaurantId,
                    formatDate(start),
                    formatDate(end)
                );
                const analytics = await api.getRestaurantAnalytics(
                    restaurantId,
                    formatDate(start),
                    formatDate(end)
                );
                setAnalytics(analytics);
                setVisits(visits);
            } catch (error) {
                console.error("Failed to fetch analytics", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [restaurantId, selectedMonth]);

    const dailyVisitsData = React.useMemo(() => {
        if (!selectedMonth) return [];
        // Parse selected month
        const [yearStr, monthStr] = selectedMonth.split('-');
        const year = parseInt(yearStr);
        const month = parseInt(monthStr); // 1-indexed

        // Get number of days in the month
        const daysInMonth = new Date(year, month, 0).getDate();

        // Count visits and segments
        const counts: Record<string, { total: number; breakfast: number; lunch: number; dinner: number; other: number }> = {};

        if (visits && visits.length) {
            visits.forEach((visit: any) => {
                if (visit.visitedAt) {
                    const visitDate = new Date(visit.visitedAt);
                    const dateStr = visitDate.toISOString().split('T')[0];
                    const hour = visitDate.getHours();

                    if (!counts[dateStr]) {
                        counts[dateStr] = { total: 0, breakfast: 0, lunch: 0, dinner: 0, other: 0 };
                    }

                    counts[dateStr].total++;

                    // 6:00 am - 10:59:59 am -> breakfast
                    // 11:00 am - 3:59:59 pm -> lunch
                    // 4:0:00 pm -> 11:59:59 pm -> dinner
                    if (hour >= 6 && hour < 11) {
                        counts[dateStr].breakfast++;
                    } else if (hour >= 11 && hour < 16) {
                        counts[dateStr].lunch++;
                    } else if (hour >= 16) {
                        counts[dateStr].dinner++;
                    } else {
                        counts[dateStr].other++;
                    }
                }
            });
        }

        // Generate full date range
        const result = [];
        for (let d = 1; d <= daysInMonth; d++) {
            const dateObj = new Date(year, month - 1, d);
            const y = dateObj.getFullYear();
            const m = String(dateObj.getMonth() + 1).padStart(2, '0');
            const day = String(dateObj.getDate()).padStart(2, '0');
            const dateStr = `${y}-${m}-${day}`;

            const dayOfWeek = dateObj.getDay();
            const isWeekend = dayOfWeek === 0 || dayOfWeek === 6; // 0 Sunday, 6 Saturday

            const dayCounts = counts[dateStr] || { total: 0, breakfast: 0, lunch: 0, dinner: 0, other: 0 };

            result.push({
                date: dateStr,
                count: dayCounts.total,
                breakfast: dayCounts.breakfast,
                lunch: dayCounts.lunch,
                dinner: dayCounts.dinner,
                other: dayCounts.other,
                isWeekend
            });
        }
        return result;
    }, [visits, selectedMonth]);

    if (loading || !analytics) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
                <CircularProgress />
            </Box>
        );
    }

    // Helpers
    const getName = (key: string, type: 'allergy' | 'diet') => {
        const map = type === 'allergy' ? allergyKeyToName : dietKeyToName;
        // Handle combined keys (e.g., "gluten, dairy" or "gluten|dairy")
        if (key.includes(',')) {
            return key.split(',').map(k => map[k.trim()] || k.trim()).join(', ');
        }
        if (key.includes('|')) {
            return key.split('|').map(k => map[k.trim()] || k.trim()).join(' | ');
        }
        return map[key] || key;
    };

    const formatHeatmapData = (coOccurrence: Record<string, number>, type: 'allergy' | 'diet') => {
        return Object.entries(coOccurrence).map(([key, value]) => {
            const [x, y] = key.split('|');
            return {
                id: getName(key, type),
                x: getName(x, type),
                y: getName(y, type),
                value
            };
        }).reduce((acc: any[], curr) => {
            let row = acc.find(r => r.id === curr.x);
            if (!row) {
                row = { id: curr.x, data: [] };
                acc.push(row);
            }
            row.data.push({ x: curr.y, y: curr.value });
            return acc;
        }, []);
    };

    // Data Transformation
    const complexityData = Object.entries(analytics.combined.complexity).map(([name, value]) => ({ name, value }));

    const allergiesPrevalence = Object.entries(analytics.allergies.prevalence)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([key, count]) => ({ name: getName(key, 'allergy'), count }));

    const dietsPrevalence = Object.entries(analytics.diets.prevalence)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 10)
        .map(([key, count]) => ({ name: getName(key, 'diet'), count }));

    const allergyHeatmapData = formatHeatmapData(analytics.allergies.coOccurrence, 'allergy');

    const sankeyNodes: { id: string }[] = [];
    const sankeyLinks: { source: string; target: string; value: number }[] = [];
    Object.entries(analytics.combined.crossTab).forEach(([dietKey, allergiesMap]) => {
        const dietName = getName(dietKey, 'diet');
        if (!sankeyNodes.find(n => n.id === dietName)) sankeyNodes.push({ id: dietName });

        Object.entries(allergiesMap).forEach(([allergyKey, count]) => {
            const allergyName = getName(allergyKey, 'allergy');
            if (!sankeyNodes.find(n => n.id === allergyName)) sankeyNodes.push({ id: allergyName });
            sankeyLinks.push({ source: dietName, target: allergyName, value: count });
        });
    });

    const unifiedProfiles = Object.entries(analytics.combined.profiles).map(([profileKey, count]) => {
        let displayName = profileKey;
        if (profileKey !== NO_PREFERENCES) {
            displayName = profileKey.split(',').map(k => {
                const trimmed = k.trim();
                return allergyKeyToName[trimmed] || dietKeyToName[trimmed] || trimmed;
            }).join(', ');
        }
        return { name: displayName, count };
    }).sort((a, b) => b.count - a.count);

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
                <Box>
                    <Typography variant="h4" gutterBottom>Dietary Insights</Typography>
                    <Card sx={{ bgcolor: 'primary.main', color: 'white', display: 'inline-block', minWidth: 200 }}>
                        <CardContent sx={{ p: '16px !important' }}>
                            <Typography variant="subtitle1">Total Users</Typography>
                            <Typography variant="h4">{analytics.metadata.totalUsers}</Typography>
                        </CardContent>
                    </Card>
                </Box>
                <Box display="flex" gap={2}>
                    <TextField
                        label="Select Month"
                        type="month"
                        value={selectedMonth}
                        onChange={handleMonthChange}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: 200 }}
                    />
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Row 0: Visits Histogram */}
                <Grid item xs={12}>
                    <Card sx={{ height: 400 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Visits Over Time</Typography>
                            <Box height={300}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={dailyVisitsData} margin={{ left: 20, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" />
                                        <YAxis allowDecimals={false} />
                                        <RechartsTooltip
                                            content={({ active, payload, label }: any) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    const [y, m, d] = (label as string).split('-').map(Number);
                                                    const date = new Date(y, m - 1, d);
                                                    const dateString = `${label} (${date.toLocaleDateString('en-US', { weekday: 'long' })})`;

                                                    return (
                                                        <div style={{
                                                            backgroundColor: '#fff',
                                                            padding: '10px',
                                                            border: '1px solid #ccc',
                                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{dateString}</p>
                                                            <p style={{ margin: 0 }}>Total Visits: {data.count}</p>
                                                            <div style={{ marginTop: '5px', fontSize: '0.9em', color: '#666' }}>
                                                                <p style={{ margin: 0 }}>Breakfast: {data.breakfast}</p>
                                                                <p style={{ margin: 0 }}>Lunch: {data.lunch}</p>
                                                                <p style={{ margin: 0 }}>Dinner: {data.dinner}</p>
                                                                {data.other > 0 && <p style={{ margin: 0 }}>Other: {data.other}</p>}
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <Bar dataKey="count" name="Visits">
                                            {dailyVisitsData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.isWeekend ? '#ffc658' : '#3f51b5'} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                            <Box mt={2} display="flex" justifyContent="center" gap={3} flexWrap="wrap">
                                <Typography variant="caption" color="textSecondary">
                                    <strong>Breakfast:</strong> 6:00 AM - 11:00 AM
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    <strong>Lunch:</strong> 11:00 AM - 4:00 PM
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                    <strong>Dinner:</strong> 4:00 PM - 12:00 AM
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Row 1: Top Allergies & Top Diets */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Top Allergies</Typography>
                            <Box height={300}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart layout="vertical" data={allergiesPrevalence} margin={{ left: 20, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11 }} />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" fill="#8884d8" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: 400 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Top Diets</Typography>
                            <Box height={300}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart layout="vertical" data={dietsPrevalence} margin={{ left: 20, right: 30 }}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis type="number" />
                                        <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11 }} />
                                        <RechartsTooltip />
                                        <Bar dataKey="count" fill="#82ca9d" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Row 2: Allergy Heatmap & Sankey (Side by Side) */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: 500 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Allergy Co-occurrence</Typography>
                            <Box height={400}>
                                {allergyHeatmapData.length > 0 ? (
                                    <ResponsiveHeatMap
                                        data={allergyHeatmapData}
                                        margin={{ top: 60, right: 90, bottom: 60, left: 90 }}
                                        valueFormat=">-.0s"
                                        axisTop={{ tickSize: 5, tickPadding: 5, tickRotation: -90, legendOffset: 46 }}
                                        axisLeft={{ tickSize: 5, tickPadding: 5, tickRotation: 0, legendOffset: -72 }}
                                        colors={{ type: 'sequential', scheme: 'blues' }}
                                        emptyColor="#555555"
                                        theme={nivoTheme}
                                        hoverTarget="cell"
                                    />
                                ) : <Typography color="textSecondary" align="center" pt={10}>No allergy co-occurrence data.</Typography>}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ height: 500 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Restriction Complexity</Typography>
                            <Box height={400}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={complexityData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={60}
                                            outerRadius={100}
                                            paddingAngle={5}
                                            dataKey="value"
                                        >
                                            {complexityData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS] || '#888'} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip
                                            content={({ active, payload }: any) => {
                                                if (active && payload && payload.length) {
                                                    const { name, value } = payload[0].payload;
                                                    const total = analytics.metadata.totalUsers;
                                                    const percentage = total > 0 ? ((value as number / total) * 100).toFixed(1) : '0';

                                                    const ranges: Record<string, string> = {
                                                        Easy: '0-1 restrictions',
                                                        Moderate: '2-3 restrictions',
                                                        Complex: '4+ restrictions'
                                                    };
                                                    const range = ranges[name as string] || '';

                                                    return (
                                                        <div style={{
                                                            backgroundColor: '#fff',
                                                            padding: '10px',
                                                            border: '1px solid #ccc',
                                                            boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                                            borderRadius: '4px'
                                                        }}>
                                                            <p style={{ margin: 0, fontWeight: 'bold' }}>
                                                                {`${name} (${range}): ${value} (${percentage}%)`}
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                        <RechartsLegend verticalAlign="bottom" height={36} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Row 3: Sankey */}
                <Grid item xs={12} md={12}>
                    <Card sx={{ height: 500 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Diet-Allergy Flow</Typography>
                            <Box height={400}>
                                {sankeyNodes.length > 0 && sankeyLinks.length > 0 ? (
                                    <ResponsiveSankey
                                        data={{ nodes: sankeyNodes, links: sankeyLinks }}
                                        margin={{ top: 40, right: 160, bottom: 40, left: 150 }}
                                        align="justify"
                                        colors={{ scheme: 'category10' }}
                                        nodeOpacity={1}
                                        nodeThickness={18}
                                        nodeInnerPadding={3}
                                        nodeSpacing={10}
                                        nodeBorderWidth={0}
                                        nodeBorderColor={{ from: 'color', modifiers: [['darker', 0.8]] }}
                                        linkOpacity={0.7}
                                        linkHoverOthersOpacity={0.1}
                                        linkContract={0}
                                        enableLinkGradient={true}
                                        labelPosition="outside"
                                        labelOrientation="horizontal"
                                        labelPadding={16}
                                        labelTextColor={{ from: 'color', modifiers: [['darker', 1]] }}
                                        theme={nivoTheme}
                                        nodeTooltip={({ node }: any) => (
                                            <div style={{ padding: 12, color: '#fff', background: '#333' }}>
                                                <strong>{node.id}</strong>
                                                <br />
                                                Value: {node.value}
                                            </div>
                                        )}
                                    />
                                ) : <Typography color="textSecondary" align="center" pt={10}>No flow data.</Typography>}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={12}>
                    <Card sx={{ height: 500 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Unified Personas</Typography>
                            <Box sx={{ width: '100%', bgcolor: 'background.paper', height: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
                                <List>
                                    {unifiedProfiles.map((profile, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem>
                                                <ListItemText
                                                    primary={profile.name}
                                                    secondary={`${profile.count} Users`}
                                                    primaryTypographyProps={{ fontWeight: 'bold' }}
                                                />
                                                <Box sx={{
                                                    width: '40%',
                                                    bgcolor: 'grey.200',
                                                    height: 10,
                                                    borderRadius: 5,
                                                    overflow: 'hidden',
                                                    ml: 2
                                                }}>
                                                    <Box sx={{
                                                        width: `${(profile.count / analytics.metadata.totalUsers) * 100}%`,
                                                        bgcolor: CHART_COLORS[index % CHART_COLORS.length],
                                                        height: '100%'
                                                    }} />
                                                </Box>
                                            </ListItem>
                                            {index < unifiedProfiles.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DietaryAnalyticsDashboard;
